import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { coursePerformanceDetails } from '../../../services/adminService';
import { decodeData } from '../../../utils/encodeAndEncode';
import { useParams } from 'react-router-dom';

const defaultProfile = '/images/profile/default-profile.png';

function CourseAnalyticsTraineeProgress() {
  const { courseId } = useParams();
  const decodedCourseId = decodeData(courseId);

  const [traineesData, setTraineesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0
  });

  // Fetch trainee progress data
  useEffect(() => {
    fetchTraineeProgress();
  }, [decodedCourseId, search, statusFilter, pagination.page]);

  const fetchTraineeProgress = async () => {
    try {
      setLoading(true);
      const response = await coursePerformanceDetails({
        course_id: decodedCourseId,
        search: search,
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter
      });

      // console.log('Trainee Progress Response:', response);

      if (response.success && response.data) {
        setTraineesData(response.data.users || []);
        setPagination(prev => ({
          ...prev,
          page: response.data.pagination?.current_page || 1,
          totalPages: response.data.pagination?.total_pages || 0,
          totalCount: response.data.pagination?.total_records || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching trainee progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <>
      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row mb-3 gap-3">
        <div className="md:w-1/2 flex justify-start items-center">
          <input
            type="search"
            className="w-full max-w-[300px] px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Search trainees..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="md:w-1/2 text-end flex justify-end items-center gap-2">
          <select
            className="w-[180px] px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All Progress</option>
            <option value="completed">Completed</option>
            <option value="in_progress">In Progress</option>
            <option value="not_started">Not Started</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="mb-3">
        <div className="w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">SL.No</th>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">User</th>
                    <th className="bg-gray-50 font-medium py-3 px-4 text-left">Course Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="3" className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : traineesData.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-4 text-gray-500">
                        No trainees found
                      </td>
                    </tr>
                  ) : (
                    traineesData.map((trainee, index) => (
                      <tr key={trainee.user_id || index} className="border-b">
                        <td className="py-3 align-middle px-4">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <img
                              src={trainee.profile_pic_url || defaultProfile}
                              alt={trainee.user_name || 'User'}
                              className="rounded-full mr-3"
                              width="40"
                              height="40"
                              onError={(e) => {
                                e.target.src = defaultProfile;
                              }}
                            />
                            <div>
                              <div className="font-medium">{trainee.user_name || 'N/A'}</div>
                              <div className="text-gray-500 text-sm">{trainee.email || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 align-middle px-4">
                          <div className="flex items-center">
                            <div className="flex-grow-1 h-1.5 w-[100px] bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary transition-all"
                                role="progressbar"
                                style={{ width: `${Number(trainee.overallCompletionPercentage || 0)}%` }}
                                aria-valuenow={Number(trainee.overallCompletionPercentage || 0)}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                            <span className="ml-2">{Math.round(Number(trainee.overallCompletionPercentage || 0))}%</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {!loading && traineesData.length > 0 && (
        <div className="flex flex-col md:flex-row gap-3 mt-4 pt-4 border-t border-gray-100">
          <div className="md:w-1/2">
            <select
              className="w-auto px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
            >
              <option value={10}>10 records per page</option>
              <option value={25}>25 records per page</option>
              <option value={50}>50 records per page</option>
            </select>
          </div>
          <div className="md:w-1/2 flex justify-end items-center gap-3">
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ width: '150px' }}
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Prev
            </button>
            <span>Page {pagination.page} of {pagination.totalPages}</span>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ width: '150px' }}
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default CourseAnalyticsTraineeProgress
